<template>
  <section class="iw-layout-grid">
    <h1>Interact Web Grid Layout</h1>
    <hr />
    <div class="iw-layout-grid">
      <h2>
        Get started
      </h2>
      <div class="iw-layout-grid__row">
        <div class="iw-layout-grid__col--span-12">
          <h3>
            HTML Structure, using BEM methodology (Blocks, Elements and Modifiers)
          </h3>
          <pre>
              <code>
                &lt;<span>div</span> <i>class</i>="<span>iw-layout-grid</span>"&gt;<br/>
                  &nbsp;&lt;<span>div</span> <i>class</i>="<span>iw-layout-grid__row</span>"&gt;<br/>
                    &nbsp;&nbsp;&lt;<span>div</span> <i>class</i>="<span>iw-layout-grid__col--span-4</span>"&gt;4 Columns&lt;<span>/div</span>&gt;<br/>
                    &nbsp;&nbsp;&lt;<span>div</span> <i>class</i>="<span>iw-layout-grid__col--span-6</span>"&gt;6 columns&lt;<span>/div</span>&gt;<br/>
                    &nbsp;&nbsp;&lt;<span>div</span> <i>class</i>="<span>iw-layout-grid__col--span-2</span>"&gt;2 columns&lt;<span>/div</span>&gt;<br/>
                  &nbsp;&lt;<span>/div</span>&gt;<br/>
                &lt;<span>/div</span>&gt;
              </code>
          </pre>

          <h3>CSS Clases</h3>
          <ul>
            <li>
              <strong>iw-layout-grid:</strong>
              Mandatory, for the layout grid element.
            </li>
            <li>
              <strong>iw-layout-grid__row:</strong>
              Mandatory, for wrapping grid col.
            </li>
            <li>
              <strong>iw-layout-grid__row-fluid:</strong>
              Optional, for wrapping grid col without space around.
            </li>
            <li>
              <strong>iw-layout-grid__row-fluid-<small>{TYPE_OF_DEVICE}</small>:</strong>
              Optional, for wrapping grid col without space around on a type of device (desktop, phone).
            </li>
            <li>
              <strong>iw-layout-grid__col:</strong>
              Mandatory, for the layout grid col.
            </li>
            <li>
              <strong>iw-layout-grid__col--span-<small>{NUMBER_OF_COLUMNS}</small>:</strong>
              Optional, specifies the number of columns.
            </li>
            <li>
              <strong>iw-layout-grid__col--span-<small>{NUMBER_OF_COLUMNS}-{TYPE_OF_DEVICE}</small>::</strong>
              Optional, specifies the number of columns on a type of device (desktop, phone).
            </li>
          </ul>
        </div>
      </div>

      <h2>Grid of default wide items, regardless of screen size</h2>
      <div class="iw-layout-grid__row row">
        <div v-for="index in 3" :key="index" class="iw-layout-grid__col box">
          4 Columns
        </div>
      </div>

      <h2>Grid of 1 column wide items, regardless of screen size</h2>
      <div class="iw-layout-grid__row row">
        <div v-for="index in 12" :key="index" class="iw-layout-grid__col--span-1 box">
          1 Colum
        </div>
      </div>

      <h2>
        Grid of differently sized items, regardless of screen size. Without space.
      </h2>
      <div class="iw-layout-grid__row iw-layout-grid__row-fluid row">
        <div class="iw-layout-grid__col--span-2 box">2 Columns</div>
        <div class="iw-layout-grid__col--span-2 box">2 Columns</div>
        <div class="iw-layout-grid__col--span-8 box">8 Columns</div>
      </div>

      <h2>
        Grid of items with tweaks at different screen sizes. Without space for Phone
      </h2>
      <div class="iw-layout-grid__row iw-layout-grid__row-fluid-phone row">
        <div class="iw-layout-grid__col--span-4-desktop iw-layout-grid__col--span-2-phone box">
          4 Columns <small>(2 mobile)</small>
        </div>
        <div class="iw-layout-grid__col--span-6-desktop iw-layout-grid__col--span-2-phone box">
          6 Columns <small>(2 mobile)</small>
        </div>
        <div class="iw-layout-grid__col--span-2-desktop iw-layout-grid__col--span-4-phone box">
          2 Columns <small>(4 mobile)</small>
        </div>
      </div>

      <h2>Grid nested within parent grid cell</h2>
      <div class="iw-layout-grid__row row">
        <div class="iw-layout-grid__col--span-8-desktop iw-layout-grid__col--span-2-phone box">
          8 Columns (2 mobile)
          <div class="iw-layout-grid__row row">
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
            <div class="iw-layout-grid__col--span-4 box">4 Columns</div>
          </div>
        </div>
        <div class="iw-layout-grid__col--span-4-desktop iw-layout-grid__col--span-2-phone box">
          4 Columns (2 mobile)
          <div class="iw-layout-grid__row row">
            <div class="iw-layout-grid__col--span-12 box">12 Columns</div>
            <div class="iw-layout-grid__col--span-12 box">12 Columns</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "StyleGuide",
  props: {}
};
</script>

<style scoped lang="scss">
$boxColor: #42b883;
$boxColorSecundary: #35495e;
$contentColor: #ddd;
$borderColor: #ccc;

body {
  font-family: monospace;
}

code > i {
  color: red;
}

code > span {
  color: blue;
}

h1 {
  text-align: center;
  font-size: 32px;
  padding: 1rem;
}

h2 {
  margin: 2rem 0 1rem 0;
}

section {
  padding: 1rem;
  max-width: 1280px;
  margin: 0 auto;
}

strong > small {
  color: grey;
}

ul > li {
  padding: 0.5rem;
}

@media (max-width: 1023px) {
  section {
    padding: 0;
  }
}

pre {
  border: 1px solid $borderColor;
  white-space: normal;
  padding: 0.5rem 1.5rem;
}

.box {
  border: 1px solid $borderColor;
  padding: 4px;
  background: $boxColorSecundary;
  min-height: 100px;
  color: #eee;
  font-size: 12px;
}

.row {
  border: 1px dashed $borderColor;
  background: #ddd;
}
</style>
